import { ChevronRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router';
import { Flex } from '@radix-ui/themes';
import { Card, type CardProps } from './Card';
import { HeaderItalic } from './HeaderItalic';
import { RadixTextWeight } from './RadixProps';
import { Text } from './Text';

export interface MenuLinkButtonProps {
  title: string;
  subtitle?: string;
  to?: string;
  weight?: RadixTextWeight;
  external?: boolean;
  destructive?: boolean;
  onClick?: () => void;
  className?: string;
  'data-testid'?: string;
  variant?: CardProps['variant'];
  as?: 'heading' | 'text';
}

/** Settings menu row — title, optional subtitle, trailing chevron. */
export function MenuLinkButton({
  title,
  subtitle,
  to,
  weight = 'bold',
  as = 'text',
  external = false,
  destructive = false,
  onClick,
  'data-testid': dataTestId,
  variant = 'surface',
  ...props
}: MenuLinkButtonProps) {
  const Icon = external ? ExternalLink : ChevronRight;

  const titleColor = destructive ? 'crimson' : undefined;

  const label =
    as === 'heading' ? (
      <HeaderItalic size="2" color={titleColor}>
        {title}
      </HeaderItalic>
    ) : (
      <Text size="2" weight={weight} color={titleColor}>
        {title}
      </Text>
    );

  const content = (
    <Flex justify="between" align="center" gap="3" width="100%">
      <Flex direction="column" gap="1" className="menu-link-button-main">
        {label}
        {subtitle && (
          <Text as="div" size="1" color="gray">
            {subtitle}
          </Text>
        )}
      </Flex>
      {destructive ? (
        <Text color="crimson" asChild>
          <Icon className="menu-link-button-icon" size={16} aria-hidden />
        </Text>
      ) : (
        <Text color="gray" asChild>
          <Icon className="menu-link-button-icon" size={16} aria-hidden />
        </Text>
      )}
    </Flex>
  );

  let innerContent;

  if (to) {
    if (external) {
      innerContent = (
        <a
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClick}
          data-testid={dataTestId}
        >
          {content}
        </a>
      );
    } else {
      innerContent = (
        <Link to={to} onClick={onClick} data-testid={dataTestId}>
          {content}
        </Link>
      );
    }
  }

  if (!innerContent) {
    innerContent = (
      <button type="button" onClick={onClick} data-testid={dataTestId}>
        {content}
      </button>
    );
  }

  return (
    <Card
      asChild
      className="menu-link-button"
      variant={variant}
      color={destructive ? 'crimson' : undefined}
      {...props}
    >
      {innerContent}
    </Card>
  );
}
